import { useMemo } from 'react';
import { useList } from '@refinedev/core';
import type { Company } from '@viq-admin/types';
import { useCompanyScope } from '../components/CompanyScopePicker';
import { LOOKUP_STALE_MS, useAllConferences, useAllUsers } from '../lookups/useLookups';
import type { SearchSources } from './searchModel';

/**
 * Everything the header search matches against, gathered from lists the
 * console already loads, so searching costs no new endpoint:
 *
 *  - active tenants: the list CompanyScopeProvider fetched at sign-in;
 *  - archived tenants: the archived companies list (`?archived=true`), which
 *    `companies` never narrows by "Operate as" (scope-exempt in dataProvider);
 *  - users and conferences: the cross-tenant lookups, which ignore the scope.
 *
 * So a scoped operator still finds every tenant's records. The three fetched
 * lists wait for `enabled` — the search turns it on the first time it is
 * opened — so a page view that never searches fetches nothing extra.
 */
export function useSearchSources(enabled: boolean): {
  sources: SearchSources;
  /** True while a list the search needs is still loading. */
  loading: boolean;
} {
  const { companies: active } = useCompanyScope();
  const archivedQuery = useList<Company>({
    resource: 'companies',
    meta: { archived: true },
    pagination: { mode: 'off' },
    queryOptions: { staleTime: LOOKUP_STALE_MS, enabled },
  });
  const archived = archivedQuery.result?.data;
  const { users, isLoading: usersLoading } = useAllUsers({ enabled });
  const { conferences, isLoading: conferencesLoading } = useAllConferences({ enabled });

  const sources = useMemo<SearchSources>(() => {
    const activeIds = new Set(active.map((c) => c.id));
    return {
      companies: [
        ...active.map((c) => ({ id: c.id, name: c.name })),
        // A tenant restored a moment ago can sit in both lists until the
        // archived list refetches; the active entry wins.
        ...(archived ?? [])
          .filter((c) => !activeIds.has(c.id))
          .map((c) => ({ id: c.id, name: c.name, archived: true })),
      ],
      users,
      conferences,
    };
  }, [active, archived, users, conferences]);

  const loading =
    enabled && (archivedQuery.query.isLoading || usersLoading || conferencesLoading);
  return { sources, loading };
}
